/**
 * TUS ITEMS Y PRECIOS COMO EL CATALOGO DEL ITEM ESTANDAR (CATALOGO-UNICO-ITEMS-T1, maqueta aprobada por Josse, Ares #43601).
 *
 * AUDIENCIA: Josse y el asesor de Maderinco, escritorio o celular, solo el negocio demo. Contrato de pantalla: la maqueta
 * https://claude.ai/artifact/FTvBEHapj9gvLDEV5c6pc7 (version 1793763895-2c02), vista "Tus items y precios".
 *
 * Buscar, filtros Todos / Precio por confirmar / Por clasificar, secciones con sus lineas plegables y una fila por
 * estandar: tipo, nombre armado, precio por unidad y Editar en su sitio. El editor (montarEditorEstandar, el mismo de la
 * fila del asistente desde MAD-LAPIZ-ITEM-T1) es el del 699 (control por
 * caracteristica, el nombre al instante, lo que la base frenaria apaga Guardar) con el precio y Duplicar como variante.
 * Lee GET plantilla/item-estandar (nexo-api 785); guarda con PUT/POST de nexo-api 788. Reglas sin DOM en
 * application/presupuestos/itemEstandar.js; lo prueba scripts/check-item-estandar.mjs.
 *
 * @module presentation/components/presupuestos/CatalogoEstandar
 */
import { PresupuestosApiClient } from '../../../infrastructure/api/PresupuestosApiClient.js';
import { leerItemEstandar, nombreArmado, fallasDe, fraseDeFalla, conCaracteristica, varianteDe, cuerpoDeEstandar, fraseDelGuardado, catalogoDeItems, precioDeEstandar, caracteristicasDelEditor, alternarValor, conPrecio } from '../../../application/presupuestos/itemEstandar.js';
import { etiquetasCortas } from '../../../application/presupuestos/asistentePasos.js';
import { formatearCOP, parsearNumeroColombiano } from '../../shared/numeroColombiano.js';
import { esc } from '../../shared/texto.js';

const MOTIVOS = {
  NOT_IMPLEMENTED: 'La API todavía no publica el ítem estándar. Se enciende sola cuando A lo sirva (nexo-api 785).',
  FORBIDDEN: 'No tienes permiso para ver el ítem estándar de este negocio.',
  NEGOCIO_AJENO: 'El ítem estándar es solo del negocio demo de Maderinco: elige ese negocio arriba.'
};
export const motivoDe = (res) => MOTIVOS[res?.error?.code] || 'No pudimos hablar con la API ahora. Intenta de nuevo en un momento.';
const UNIDAD = { m2: 'm²', 'm²': 'm²', ml: 'ml', m: 'm', und: 'und', un: 'und', gnral: 'global', gl: 'global', m3: 'm³' };
const unidadBonita = (u) => UNIDAD[String(u || '').toLowerCase()] || (u ? String(u) : 'und');
// Pesos enteros con su signo, como la maqueta ('$ 274.000'): formatearCOP no pone el signo y muestra centavos.
const plata = (n) => '$ ' + formatearCOP(Math.round(Number(n) || 0));
const hoy = () => new Date().toISOString().slice(0, 10);

// EDITOR-ITEM-DISENO-T1 (maqueta 1793770085-ef18): cada caracteristica con sus opciones como chips de 44 px. Lista = una
// sola y "No lleva"; casillas = varias; numero = el campo y hasta 4 valores de la plantilla a un toque.
function grupoHtml(c, valores) {
  const v = valores || [];
  const chip = (x, varios) => `<button type="button" class="ce-op" data-ce-valor="${esc(c.nombre)}" data-v="${esc(x)}"${varios ? ' data-varios' : ''} aria-pressed="${v.includes(x)}">${esc(x)}</button>`;
  if (c.control === 'numero') {
    return `<div class="ce-car"><span>${esc(c.nombre)}${c.unidad ? ` (${esc(c.unidad)})` : ''}</span><div class="ce-ops">
      <input type="text" inputmode="decimal" class="ce-num" data-ce-car="${esc(c.nombre)}" value="${esc(v[0] || '')}" aria-label="${esc(c.nombre)}">
      ${c.opciones.slice(0, 4).map((x) => chip(x, false)).join('')}</div></div>`;
  }
  const varios = c.control === 'casillas';
  return `<div class="ce-car"><span>${esc(c.nombre)}${varios ? ' (puedes marcar varias)' : ''}</span><div class="ce-ops" role="group" aria-label="${esc(c.nombre)}">
    ${c.opciones.map((x) => chip(x, varios)).join('')}
    ${varios ? '' : `<button type="button" class="ce-op ce-op-no" data-ce-valor="${esc(c.nombre)}" data-v="" aria-pressed="${!v.length}">No lleva</button>`}</div></div>`;
}

const fechaBonita = (f) => (f === hoy() ? 'hoy' : f.split('-').reverse().join('/'));
const vigenciaTexto = (b, referencia) => [
  b.precio === null || b.precio === undefined ? 'sin precio: queda por confirmar' : `vigente desde ${fechaBonita(b.vigenteDesde || hoy())}`,
  referencia ? `referencia de la plantilla ${plata(referencia)}` : ''
].filter(Boolean).join(' · ');
const precioEnCampo = (p) => (p === null || p === undefined ? '' : Number.isInteger(Number(p)) ? formatearCOP(Number(p)) : String(p).replace('.', ','));

/**
 * El editor como la maqueta: el nombre que queda arriba y grande, a que alcanza el cambio, el tipo y lo que distingue
 * como chips, lo demas en "Mas caracteristicas", el precio con su vigencia y Guardar. extra = { distingue, referencia,
 * alAbrir (las caracteristicas al abrir, que fijan los grupos) }.
 */
function editorHtml(linea, b, unidad, extra = {}) {
  const pl = linea.plantilla;
  const fallas = fallasDe(pl, b.caracteristicas);
  const { arriba, mas } = caracteristicasDelEditor(pl, extra.alAbrir || b.caracteristicas, extra.distingue);
  return `<section class="ce-editor" data-ce-editor aria-label="Editar el ítem">
    <div class="ce-cabeza"><small>${b.id ? 'Nombre que queda' : 'Variante nueva · nombre que queda'}</small><b class="ce-nombre" data-ce-nombre aria-live="polite">${esc(nombreArmado(linea.linea, b.tipo, pl, b.caracteristicas))}</b></div>
    <p class="ce-alcance">${b.id ? 'Cambiar este ítem lo cambia en <b>Tus ítems y precios</b> y en los próximos presupuestos.' : 'La variante queda en <b>Tus ítems y precios</b> para los próximos presupuestos.'}</p>
    ${pl.tipos.length ? `<div class="ce-car"><span>Tipo</span><div class="ce-ops" role="group" aria-label="Tipo">${pl.tipos.map((t) => `<button type="button" class="ce-op" data-ce-tipo="${esc(t)}" aria-pressed="${b.tipo === t}">${esc(t)}</button>`).join('')}</div></div>` : ''}
    ${arriba.map((c) => grupoHtml(c, b.caracteristicas[c.nombre])).join('')}
    ${mas.length ? `<details class="ce-mas"><summary>Más características (${mas.length})</summary>${mas.map((c) => grupoHtml(c, b.caracteristicas[c.nombre])).join('')}</details>` : ''}
    ${b.id ? `<div class="ce-precio"><label for="ce-precio">Precio por ${unidadBonita(unidad)}<input id="ce-precio" type="text" inputmode="decimal" class="ce-num" data-ce-precio value="${esc(precioEnCampo(b.precio))}" placeholder="por confirmar"></label>
      <span class="ce-vigencia" data-ce-vigencia>${esc(vigenciaTexto(b, extra.referencia))}</span></div>` : ''}
    ${fallas.length ? `<ul class="ce-fallas" data-ce-fallas>${fallas.map((f) => `<li>${esc(fraseDeFalla(f))}</li>`).join('')}</ul>` : ''}
    <div class="ce-fila">
      <button type="button" class="ce-guardar" data-ce-guardar${fallas.length ? ' disabled' : ''}>${b.id ? 'Guardar' : 'Crear variante'}</button>
      <button type="button" class="ce-boton" data-ce-cancelar>Cancelar</button>
      ${b.id ? '<button type="button" class="ce-boton" data-ce-duplicar>Duplicar como variante</button>' : ''}
      <span class="ce-resultado" data-ce-resultado role="status"></span>
    </div></section>`;
}

/** Lo pintado, puro sobre la lectura y el estado (para el guardia). estado: { filtro, buscar, abiertas:Set, borrador } */
export function catalogoHtml(lectura, estado) {
  const cat = catalogoDeItems(lectura, estado);
  const filtros = [['todos', `Todos (${cat.cuentas.todos})`], ['por_confirmar', `Precio por confirmar (${cat.cuentas.porConfirmar})`], ['por_clasificar', `Por clasificar (${cat.cuentas.porClasificar})`]];
  const cabeza = `<p class="ce-ayuda">Cada ítem con su nombre armado, su tipo y su precio. Es la misma lista que usa el asistente: lo que cambies aquí sale en el próximo presupuesto.</p>
    <input type="search" class="ce-buscar" data-ce-buscar placeholder="Buscar: teja, cercha, ventana…" aria-label="Buscar en tus ítems" value="${esc(estado.buscar || '')}">
    <div class="ce-filtros" role="group" aria-label="Filtro">${filtros.map(([k, t]) => `<button type="button" class="ce-filtro" data-ce-filtro="${k}" aria-pressed="${estado.filtro === k}">${t}</button>`).join('')}</div>`;
  if (estado.filtro === 'por_clasificar') {
    return cabeza + `<p class="ce-ayuda">Ítems de la plantilla que todavía no caen en ninguna línea. Clasificarlos desde aquí está pendiente de la API.</p>
      ${cat.porClasificar.map((s) => `<section class="ce-clasificar" data-ce-grupo><b>${esc(s.seccion)} · ${s.cuantos} sin línea</b>
        ${s.items.map((i) => `<p>${esc(i.descripcion || i.ref)}${i.precioRef !== null ? ` <span class="ce-num">${plata(i.precioRef)} / ${unidadBonita(i.unidad)}</span>` : ''}</p>`).join('')}</section>`).join('')}`;
  }
  const cuerpo = cat.secciones.map((s) => `<section class="ce-seccion"><h2>${esc(s.nombre)}</h2>
    ${s.lineas.map((l) => `<details class="ce-linea" data-ce-linea="${l.n}"${estado.abiertas.has(l.n) || estado.buscar || estado.borrador?.n === l.n ? ' open' : ''}>
      <summary><b>${esc(l.linea)}</b><span>${l.estandar.length} ${l.estandar.length === 1 ? 'ítem' : 'ítems'}</span></summary>
      ${l.estandar.map((e) => {
        const p = precioDeEstandar(e);
        const abierto = estado.borrador && estado.borrador.n === l.n && estado.borrador.desde === e.id;
        return `<div class="ce-item" data-ce-item="${esc(e.id)}">
          <div class="ce-item-nombre">${e.tipo ? `<span class="ce-chip">${esc(e.tipo)}</span>` : ''}${p.porConfirmar ? '<span class="ce-chip ce-chip-ref">precio por confirmar</span>' : ''}<br><b>${esc(e.nombre)}</b></div>
          <div class="ce-item-precio"><span class="ce-num">${p.valor === null ? '—' : plata(p.valor)}</span><small>por ${unidadBonita(e.unidad)}</small></div>
          ${abierto ? '<div class="ce-editor-host" data-ce-editor-host></div>' : `<div class="ce-item-acciones"><button type="button" class="ce-boton" data-ce-editar="${esc(e.id)}" aria-label="Editar ${esc(e.nombre)}">Editar</button></div>`}
        </div>`;
      }).join('')}</details>`).join('')}</section>`).join('');
  return cabeza + (cuerpo || '<p class="ce-ayuda">Nada coincide con esa búsqueda.</p>');
}


/** El borrador con que abre el editor de un estandar: sus valores copiados (la variante no comparte arreglos). */
export const borradorDe = (est) => ({ id: est.id, tipo: est.tipo, caracteristicas: JSON.parse(JSON.stringify(est.caracteristicas || {})), precio: est.precio, vigenteDesde: est.vigenteDesde, revision: est.revision });

/**
 * MAD-LAPIZ-ITEM-T1 (maqueta 1793770085-ef18): el editor de un item estandar montado en $cont, el mismo en Tus items y
 * precios y en la fila del asistente. linea = la de la lectura (n, linea, plantilla, estandar); estandar = el que se
 * edita; borrador = el que venia escrito (si el que lo monta se repinto). alCambiar(borrador) en cada cambio,
 * alCerrar() con Cancelar, alGuardar(lectura, respuesta) despues de guardar y releer el item estandar.
 * EDITOR-ITEM-DISENO-T1: distingue = lo que separa las opciones de la linea (sin el, se calcula con etiquetasCortas);
 * referencia = el precio de la plantilla que se muestra junto a la vigencia.
 */
export function montarEditorEstandar($cont, { linea, estandar, borrador = null, distingue = null, referencia = null, alCambiar = () => {}, alCerrar = () => {}, alGuardar = () => {} }) {
  if (!$cont || !linea || !estandar) return;
  let b = borrador || borradorDe(estandar);
  // Los grupos se fijan con lo que el item tenia al abrir: marcar algo en "Mas caracteristicas" no lo hace saltar arriba.
  const extra = {
    alAbrir: JSON.parse(JSON.stringify(b.caracteristicas || {})),
    distingue: distingue || etiquetasCortas((linea.estandar || []).map((e) => ({ etiqueta: e.nombre, estandar: e })), linea.plantilla).distingue,
    referencia: referencia ?? estandar.precioRefMin ?? null
  };
  let masAbierto = false;
  const poner = (nuevo) => { b = nuevo; alCambiar(b); };
  const pintar = () => {
    $cont.innerHTML = editorHtml(linea, b, estandar.unidad, extra);
    const $m = $cont.querySelector('.ce-mas');
    if ($m) { $m.open = masAbierto; $m.addEventListener('toggle', () => { masAbierto = $m.open; }); }
  };
  // Al escribir solo cambian el nombre y el boton: el campo no pierde el foco.
  const refrescar = () => {
    const $n = $cont.querySelector('[data-ce-nombre]');
    if ($n) $n.textContent = nombreArmado(linea.linea, b.tipo, linea.plantilla, b.caracteristicas);
    const $g = $cont.querySelector('[data-ce-guardar]');
    if ($g) $g.disabled = fallasDe(linea.plantilla, b.caracteristicas).length > 0;
  };
  pintar();
  $cont.addEventListener('input', (e) => {
    const t = e.target;
    if (t.matches('input[type="text"][data-ce-car]')) {
      poner(conCaracteristica(b, t.dataset.ceCar, [t.value])); refrescar();
      // El chip con el mismo valor se marca solo: escribir 12 o tocar 12 es lo mismo.
      $cont.querySelectorAll(`[data-ce-valor="${CSS.escape(t.dataset.ceCar)}"]`).forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.v === t.value.trim())));
      return;
    }
    if (t.matches('[data-ce-precio]')) {
      const v = String(t.value).trim();
      poner(conPrecio(b, v ? parsearNumeroColombiano(v) : null, estandar, hoy()));
      const $v = $cont.querySelector('[data-ce-vigencia]');
      if ($v) $v.textContent = vigenciaTexto(b, extra.referencia);
    }
  });
  // Al salir de un campo de numero se repinta: aparecen las fallas que la base frenaria.
  $cont.addEventListener('change', (e) => { if (e.target.matches('input[type="text"][data-ce-car]')) pintar(); });
  $cont.addEventListener('click', async (e) => {
    const btn = e.target.closest('button');
    if (!btn || !$cont.contains(btn)) return;
    if (btn.dataset.ceTipo !== undefined) { poner({ ...b, tipo: btn.dataset.ceTipo }); pintar(); $cont.querySelector(`[data-ce-tipo="${CSS.escape(btn.dataset.ceTipo)}"]`)?.focus(); return; }
    if (btn.dataset.ceValor !== undefined) {
      const { ceValor: nombre, v } = btn.dataset;
      poner(alternarValor(b, nombre, v, btn.hasAttribute('data-varios'))); pintar();
      $cont.querySelector(`[data-ce-valor="${CSS.escape(nombre)}"][data-v="${CSS.escape(v)}"]`)?.focus();
      return;
    }
    if (btn.hasAttribute('data-ce-cancelar')) { alCerrar(); return; }
    if (btn.hasAttribute('data-ce-duplicar')) { poner(varianteDe(estandar)); pintar(); return; }
    if (!btn.hasAttribute('data-ce-guardar')) return;
    const $r = $cont.querySelector('[data-ce-resultado]');
    const v = cuerpoDeEstandar(linea, b);
    if (!v.ok) { $r.textContent = v.fallas.map(fraseDeFalla).join(' '); return; }
    btn.disabled = true;
    const r = b.id ? await PresupuestosApiClient.guardarItemEstandar(b.id, v.cuerpo) : await PresupuestosApiClient.crearItemEstandar(v.cuerpo);
    btn.disabled = false;
    if (!r.ok) { $r.textContent = r.error?.code === 'FORBIDDEN' || r.error?.code === 'NEGOCIO_AJENO' ? motivoDe(r) : fraseDelGuardado(r); return; }
    // Guardado: se relee para mostrar el nombre que armo la base.
    const res = await PresupuestosApiClient.itemEstandar();
    alGuardar((res.ok && leerItemEstandar(res)) || null, r);
  });
}

/** Monta el catalogo en $host: lee, pinta y conecta buscar, filtros y Editar; el editor es montarEditorEstandar. */
export async function montarCatalogoEstandar($host) {
  if (!$host) return;
  $host.innerHTML = '<p class="ce-ayuda" aria-busy="true">Cargando tus ítems…</p>';
  const res = await PresupuestosApiClient.itemEstandar();
  let lectura = res.ok ? leerItemEstandar(res) : null;
  if (!lectura) {
    $host.innerHTML = `<p class="ce-error" data-ce-error>${esc(res.ok ? 'Este negocio todavía no tiene ítem estándar cargado.' : motivoDe(res))}</p>`;
    return;
  }
  // borrador = { n, desde, ...lo escrito }: sobrevive a que el catalogo se repinte (buscar, abrir una linea).
  const estado = { filtro: 'todos', buscar: '', abiertas: new Set(), borrador: null };
  const lineaDe = (n) => lectura.capitulos.flatMap((c) => c.lineas).find((l) => l.n === n);
  const pintar = () => {
    $host.innerHTML = catalogoHtml(lectura, estado);
    const $e = $host.querySelector('[data-ce-editor-host]');
    if (!$e || !estado.borrador) return;
    const { n, desde, ...escrito } = estado.borrador;
    const l = lineaDe(n);
    montarEditorEstandar($e, {
      linea: l, estandar: l?.estandar.find((x) => x.id === desde), borrador: escrito,
      alCambiar: (b) => { estado.borrador = { ...b, n, desde }; },
      alCerrar: () => { estado.borrador = null; pintar(); },
      alGuardar: (nueva, r) => {
        lectura = nueva || lectura;
        estado.borrador = null; estado.abiertas.add(n); pintar();
        $host.querySelector(`[data-ce-linea="${n}"]`)?.insertAdjacentHTML('beforeend', `<p class="ce-resultado" role="status" data-ce-guardado>${esc(fraseDelGuardado(r))}</p>`);
      }
    });
  };
  pintar();

  $host.addEventListener('toggle', (e) => {
    const d = e.target.closest?.('[data-ce-linea]');
    if (!d) return;
    const n = Number(d.dataset.ceLinea);
    if (d.open) estado.abiertas.add(n); else estado.abiertas.delete(n);
  }, true);
  $host.addEventListener('input', (e) => {
    const t = e.target;
    if (!t.matches('[data-ce-buscar]')) return;
    estado.buscar = t.value; const pos = t.selectionStart; pintar();
    const $b = $host.querySelector('[data-ce-buscar]'); $b.focus(); $b.setSelectionRange(pos, pos);
  });
  $host.addEventListener('click', (e) => {
    const b = e.target.closest('button');
    if (!b) return;
    if (b.dataset.ceFiltro) { estado.filtro = b.dataset.ceFiltro; estado.borrador = null; pintar(); return; }
    if (b.dataset.ceEditar) {
      const n = Number(b.closest('[data-ce-linea]').dataset.ceLinea);
      const est = lineaDe(n)?.estandar.find((x) => x.id === b.dataset.ceEditar);
      if (!est) return;
      estado.borrador = { ...borradorDe(est), n, desde: est.id };
      pintar();
      $host.querySelector('[data-ce-editor]')?.scrollIntoView({ block: 'nearest' });
    }
  });
}
